import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import useTranscriptionSettings from './useTranscriptionSettings';

// Move state outside the function to make it shared across all instances
const audioDevices = ref([]);
const selectedAudioDevice = ref(null);
const missingAudioPermission = ref(false);

// Track if the composable has been initialized
let isInitialized = false;

export default function useAudioDevices() {
    const store = useStore();
    const transcriptionSettings = useTranscriptionSettings();
    
    const audioDevicesForSelect = computed(() => {
        return audioDevices.value.map(device => ({
            deviceId: device.deviceId,
            label: device.label,
        }));
    });
    
    // Initialize only once, but don't fetch devices automatically
    if (!isInitialized) {
        isInitialized = true;
    }
    
    async function fetchAudioDevices() {
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
            audioDevices.value = [];
            throw new Error('Media devices API not supported in this browser');
        }

        let stream = null;
        try {
            // Request permission to access audio devices
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            // Get list of all media devices
            const devices = await navigator.mediaDevices.enumerateDevices();

            // Filter for audio input devices (microphones)
            audioDevices.value = devices.filter(device => device.kind === 'audioinput')
                .filter(device => !device.label.includes('(Virtual)'));

            // Only try to select a device if we have devices and don't already have one selected
            if (!selectedAudioDevice.value && audioDevicesForSelect.value.length > 0) {
                const userSettingDevice = audioDevicesForSelect.value.find(device => {
                    return transcriptionSettings.audioDevice.value === device.deviceId;
                });

                selectedAudioDevice.value = userSettingDevice || audioDevicesForSelect.value[0];
            }

            missingAudioPermission.value = false;

        } catch (error) {
            audioDevices.value = [];
            if (error.name === 'NotAllowedError') {
                onAudioDenied();
            } else {
                throw error;
            }
        } finally {
            // Clean up the media stream if it was created
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        }
    }

    function onAudioDenied() {
        selectedAudioDevice.value = null;
        audioDevices.value = [];
        missingAudioPermission.value = true;
    }

    return {
        audioDevices,
        selectedAudioDevice,
        missingAudioPermission,
        audioDevicesForSelect,
        fetchAudioDevices,
        onAudioDenied
    };
} 